import {
  RenoReviewDevice,
  RenoReviewQueueEntry,
} from "@/data/models/RenoReviewEntry";

/**
 * Traversal state for the RENO review stepper (D19).
 *
 * "Next" is device-major, chain-minor: the next older chain of the **same** device, and past that
 * device's oldest qualifying chain, the **next device** positioned at its **newest** chain.
 * "Previous" is the exact mirror, landing on the previous device's **oldest** chain.
 *
 * Kept as pure functions over an immutable state object, deliberately knowing nothing about
 * Firestore or Vue, because this is the part most likely to be subtly wrong and it needs to be
 * testable without either. The caller owns all I/O: it loads a device's chains and hands them in.
 *
 * Two cursors, not one, because a device's chain list is loaded separately from the device track.
 * Crossing a device boundary advances the first and resets the second.
 */

export interface RenoReviewCursor {
  /** The device track, in serial order. */
  devices: RenoReviewDevice[];
  /** Index into {@link devices}. -1 when the cursor is empty. */
  deviceIndex: number;
  /** The current device's chains, `chainStart` descending. Empty for a device that filtered out. */
  chains: RenoReviewQueueEntry[];
  /** Index into {@link chains}. -1 when the current device has none. */
  chainIndex: number;
}

/** What a step asks the caller to do next. */
export enum RenoReviewStepOutcome {
  /** The cursor moved; {@link RenoReviewCursor.chains} already holds the right list. */
  MOVED = "MOVED",
  /**
   * The cursor advanced to a device whose chains are not loaded yet.
   *
   * The caller must load them and call {@link settleOnDevice}. This is also how the empty-device
   * skip works: if the load comes back empty, calling `settleOnDevice` again continues in the same
   * direction rather than stranding the reviewer on a dead end.
   */
  NEEDS_DEVICE_CHAINS = "NEEDS_DEVICE_CHAINS",
  /** Already at the very first or very last drying; nothing changed. */
  AT_BOUNDARY = "AT_BOUNDARY",
}

export interface RenoReviewStep {
  cursor: RenoReviewCursor;
  outcome: RenoReviewStepOutcome;
  /** Set when {@link RenoReviewStepOutcome.NEEDS_DEVICE_CHAINS}: the device to load chains for. */
  deviceToLoad?: RenoReviewDevice;
  /**
   * Which end of the newly loaded list to land on.
   *
   * Forward traversal lands on the newest chain (index 0); backward lands on the oldest (last
   * index). Getting this wrong is the classic bug in a bidirectional stepper, so the direction is
   * carried explicitly rather than inferred.
   */
  landOn?: "newest" | "oldest";
}

/** An empty cursor, before any device track is loaded. */
export function emptyCursor(): RenoReviewCursor {
  return { devices: [], deviceIndex: -1, chains: [], chainIndex: -1 };
}

/**
 * Starts a traversal on a freshly loaded device track.
 *
 * @param devices The device track, serial ascending.
 * @param resumeSerialNo Serial to open on, for resuming a pass after a page reload. The **whole**
 *   track is still kept, so Zurück can walk back past it; only the opening position moves. Ignored
 *   when absent or not in the track, which is the plain "start at the beginning" case.
 * @returns A step asking for that device's chains, or `AT_BOUNDARY` if the track is empty.
 */
export function startCursor(
  devices: RenoReviewDevice[],
  resumeSerialNo?: string
): RenoReviewStep {
  if (devices.length === 0) {
    return { cursor: emptyCursor(), outcome: RenoReviewStepOutcome.AT_BOUNDARY };
  }

  const resumeIndex = resumeSerialNo
    ? devices.findIndex((device) => device.serialNo === resumeSerialNo)
    : -1;
  const deviceIndex = resumeIndex < 0 ? 0 : resumeIndex;

  return {
    cursor: { devices, deviceIndex, chains: [], chainIndex: -1 },
    outcome: RenoReviewStepOutcome.NEEDS_DEVICE_CHAINS,
    deviceToLoad: devices[deviceIndex],
    landOn: "newest",
  };
}

/**
 * Advances to the next older chain, crossing into the next device when needed.
 *
 * @param cursor Current position.
 * @returns The step to apply.
 */
export function next(cursor: RenoReviewCursor): RenoReviewStep {
  if (cursor.chainIndex >= 0 && cursor.chainIndex < cursor.chains.length - 1) {
    return {
      cursor: { ...cursor, chainIndex: cursor.chainIndex + 1 },
      outcome: RenoReviewStepOutcome.MOVED,
    };
  }

  const nextDeviceIndex = cursor.deviceIndex + 1;

  if (nextDeviceIndex >= cursor.devices.length) {
    return { cursor, outcome: RenoReviewStepOutcome.AT_BOUNDARY };
  }

  return {
    cursor: {
      ...cursor,
      deviceIndex: nextDeviceIndex,
      chains: [],
      chainIndex: -1,
    },
    outcome: RenoReviewStepOutcome.NEEDS_DEVICE_CHAINS,
    deviceToLoad: cursor.devices[nextDeviceIndex],
    landOn: "newest",
  };
}

/**
 * Steps back to the next newer chain, crossing into the previous device when needed.
 *
 * Crossing backwards lands on the previous device's **oldest** chain, so that stepping back then
 * forward returns to where you were.
 *
 * @param cursor Current position.
 * @returns The step to apply.
 */
export function previous(cursor: RenoReviewCursor): RenoReviewStep {
  if (cursor.chainIndex > 0) {
    return {
      cursor: { ...cursor, chainIndex: cursor.chainIndex - 1 },
      outcome: RenoReviewStepOutcome.MOVED,
    };
  }

  const previousDeviceIndex = cursor.deviceIndex - 1;

  if (previousDeviceIndex < 0) {
    return { cursor, outcome: RenoReviewStepOutcome.AT_BOUNDARY };
  }

  return {
    cursor: {
      ...cursor,
      deviceIndex: previousDeviceIndex,
      chains: [],
      chainIndex: -1,
    },
    outcome: RenoReviewStepOutcome.NEEDS_DEVICE_CHAINS,
    deviceToLoad: cursor.devices[previousDeviceIndex],
    landOn: "oldest",
  };
}

/**
 * Supplies the chains for the device the cursor currently points at.
 *
 * When `chains` is empty the device filtered out entirely, so this continues in `direction` instead
 * of settling — that is the "a device can become empty after filtering" case from §5.4, which must
 * be indistinguishable from the device not existing.
 *
 * @param cursor Cursor awaiting chains.
 * @param chains The loaded chains, `chainStart` descending.
 * @param direction Which end to land on, and which way to continue if the list is empty.
 * @returns A settled step, a request for the next device's chains, or `AT_BOUNDARY`.
 */
export function settleOnDevice(
  cursor: RenoReviewCursor,
  chains: RenoReviewQueueEntry[],
  direction: "newest" | "oldest"
): RenoReviewStep {
  if (chains.length > 0) {
    return {
      cursor: {
        ...cursor,
        chains,
        chainIndex: direction === "newest" ? 0 : chains.length - 1,
      },
      outcome: RenoReviewStepOutcome.MOVED,
    };
  }

  // Empty after filtering: skip this device and keep going the same way.
  const skipped: RenoReviewCursor = { ...cursor, chains: [], chainIndex: -1 };

  return direction === "newest" ? next(skipped) : previous(skipped);
}

/** The drying currently under review, or null when the cursor has not settled. */
export function currentEntry(
  cursor: RenoReviewCursor
): RenoReviewQueueEntry | null {
  if (cursor.chainIndex < 0 || cursor.chainIndex >= cursor.chains.length) {
    return null;
  }

  return cursor.chains[cursor.chainIndex];
}

/**
 * Moves within the current device's loaded chains to a specific drying, for resuming a pass.
 *
 * Separate from {@link startCursor}'s `resumeSerialNo` because a device's chains are loaded after the
 * cursor lands on it: the device is chosen while the list is still empty, the chain once it is there.
 *
 * @returns The cursor unchanged when the chain is not in the list, which is what a reload sees when
 *   the drying has since been rated away by `hideRated`, suppressed, or filtered out.
 */
export function landOnChain(
  cursor: RenoReviewCursor,
  chainId: string
): RenoReviewCursor {
  const chainIndex = cursor.chains.findIndex(
    (chain) => chain.chainId === chainId
  );

  return chainIndex < 0 ? cursor : { ...cursor, chainIndex };
}

/** The device currently under review, or null when the cursor is empty. */
export function currentDevice(
  cursor: RenoReviewCursor
): RenoReviewDevice | null {
  if (cursor.deviceIndex < 0 || cursor.deviceIndex >= cursor.devices.length) {
    return null;
  }

  return cursor.devices[cursor.deviceIndex];
}

/**
 * The device whose chains are worth prefetching, if any.
 *
 * Prefetching while the reviewer is on a device's last chain hides the boundary latency, and also
 * absorbs the empty-device skip (§5.4). Returns null when there is nothing to prefetch.
 */
export function deviceToPrefetch(
  cursor: RenoReviewCursor
): RenoReviewDevice | null {
  const onLastChain =
    cursor.chainIndex >= 0 && cursor.chainIndex === cursor.chains.length - 1;

  if (!onLastChain) {
    return null;
  }

  return cursor.devices[cursor.deviceIndex + 1] ?? null;
}

/**
 * Human-facing position **within the current device's** dryings, e.g. "drying 2 of 7".
 *
 * Exact, because that one list is fully loaded. There is deliberately no pass-wide equivalent: only
 * one device's chains are ever in memory, and a running index across devices cannot be recovered
 * after a reload or a deep link, so the label names its scope instead.
 */
export function localPosition(
  cursor: RenoReviewCursor
): { index: number; total: number } | null {
  if (cursor.chainIndex < 0) {
    return null;
  }

  return { index: cursor.chainIndex + 1, total: cursor.chains.length };
}
